import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Fade,
  useTheme,
} from "@mui/material";
import { useIntl } from "react-intl";

export const CourseSelection = ({
  universitiesData,
  loadingUniversites,
  courseOptions,
  loadingCourses,
  formValues,
  onUniversityChange,
  onCourseInputChange,
  onCourseSelected,
}) => {
  const intl = useIntl();
  const muiTheme = useTheme();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  return (
    <Paper
      elevation={8}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        maxWidth: { xs: '100%', md: '600px' },
        width: '100%',
        mx: { xs: 0, sm: 2 },
        boxSizing: 'border-box',
      }}
    >
      <Stack spacing={3}>
        <Typography
          variant="h6"
          sx={{
            color: muiTheme.palette.grey[700],
            fontWeight: 600,
            textAlign: 'center',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            maxWidth: '100%',
          }}
        >
          Wähle deine Universität und deinen Kurs
        </Typography>

        <Autocomplete
          options={(universitiesData && universitiesData.universities) || []}
          loading={loadingUniversites}
          getOptionLabel={(option) => option.name}
          onChange={onUniversityChange}
          noOptionsText="Keine Universitäten gefunden"
          loadingText="Lade Universitäten..."
          renderInput={(params) => (
            <TextField
              {...params}
              label={intl.formatMessage({
                id: "landing-page.choose-uni-button",
              })}
              variant="outlined"
              fullWidth
              data-testid="landing-page.choose-uni-button"
              aria-label="Universität auswählen"
              helperText={formValues.university ? "Universität ausgewählt" : "Wählen Sie eine Universität aus"}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          )}
          sx={{
            '& .MuiAutocomplete-root': {
              borderRadius: 2,
            }
          }}
        />

        {formValues.university && isMounted && (
          <Fade in={isMounted} timeout={500}>
            <Box>
              <Autocomplete
                options={courseOptions}
                loading={loadingCourses}
                filterOptions={(options) => options}
                getOptionLabel={(option) => option.title}
                onInputChange={onCourseInputChange}
                noOptionsText="Keine Kurse gefunden"
                loadingText="Lade Kurse..."
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.title}
                      </Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({
                      id: "landing-page.choose-course-button",
                    })}
                    variant="outlined"
                    fullWidth
                    data-testid="landing-page.choose-course-button"
                    aria-label="Kurs auswählen"
                    helperText={
                      loadingCourses 
                        ? "Lade Kurse..." 
                        : formValues.course 
                          ? "Kurs ausgewählt" 
                          : "Geben Sie den Kursnamen ein"
                    }
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>
          </Fade>
        )}

        {formValues.course && isMounted && (
          <Fade in={isMounted} timeout={500}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`${formValues.university} - ${formValues.course}`}
                color="primary"
                variant="outlined"
                sx={{ 
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    fontSize: '0.9rem',
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={onCourseSelected}
                size="large"
                aria-label={`Weiter zum Kurs ${formValues.course}`}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${muiTheme.palette.primary.main} 0%, #F86B6B 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, #F86B6B 0%, ${muiTheme.palette.primary.main} 100%)`,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(232, 121, 117, 0.3)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Weiter zum Kurs
              </Button>
            </Box>
          </Fade>
        )}
      </Stack>
    </Paper>
  );
};
